/**
 * useHandleAddressForm Hook
 * Handles address form initialization and default values
 * @see mobile: module-customer/src/modules/addresses/hooks/use-handle-address-form.hooks.ts
 */

'use client';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AddressTypeEnum, type CustomerAddress, type CustomerAddressRegion } from '../../../types';
import type { AddressFormType } from '../components/address-form';

const defaultRegionId = '-1';

/**
 * Type guard to check if region is an object (CustomerAddressRegion) vs string
 */
const isRegionObject = (region: string | CustomerAddressRegion | null | undefined): region is CustomerAddressRegion => {
    return typeof region === 'object' && region !== null;
};

export const useHandleAddressForm = (shippingAddress?: CustomerAddress) => {
    const isUpdatingAddress = shippingAddress && shippingAddress?.id;
    const { t } = useTranslation('customer');

    const defaultValues: AddressFormType = useMemo(() => {
        const region = shippingAddress?.region;
        const regionIsObject = isRegionObject(region);
        const regionCode = regionIsObject ? (region.region_code ?? '') : '';
        const regionId = regionIsObject ? (region.region_id ?? defaultRegionId) : defaultRegionId;

        // Calculate regionName without nested ternary
        let regionName = '';
        if (regionIsObject) {
            regionName = region.region ?? '';
        } else if (typeof region === 'string') {
            regionName = region;
        }

        return {
            name: `${shippingAddress?.firstname ?? ''} ${shippingAddress?.lastname ?? ''}`.trim(),
            phoneNumber: shippingAddress?.telephone ?? '',
            location: {
                region: {
                    code: regionCode,
                    id: `${regionId}`,
                    name: regionName,
                },
                district: {
                    id: `${shippingAddress?.district_id ?? ''}`,
                    name: shippingAddress?.district ?? '',
                    region_code: regionCode,
                    region_id: `${regionId}`,
                },
                ward: {
                    id: `${shippingAddress?.ward_id ?? ''}`,
                    name: shippingAddress?.ward ?? '',
                    district_id: `${shippingAddress?.district_id ?? ''}`,
                },
            },
            street: (shippingAddress?.street ?? []).join(', '),
            addressType: (shippingAddress as { address_type?: string })?.address_type ?? AddressTypeEnum.Office,
            defaultAddress: !!shippingAddress?.default_shipping,
        };
    }, [shippingAddress]);

    const primaryButtonLabel = useMemo(
        () => (isUpdatingAddress ? t('customer_text_update') : t('customer_text_create')),
        [isUpdatingAddress, t]
    );

    return {
        defaultValues,
        isUpdatingAddress,
        primaryButtonLabel,
    };
};
